import { Button } from '@/components/ui/button';
import { ASSET_KIND_LABEL } from '@/lib/mediatheque-labels';
import type { AmbianceAssetKind } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * The filter bar of the Médiathèque (§16 of the issue #121 grill): free text
 * plus a kind toggle, and nothing else - `ambianceAssetQuerySchema` only
 * knows `q` and `kind`, so a third control here would filter on a dimension
 * the base cannot answer.
 *
 * A fresh component and not `media-picker.tsx` reused: that modal is a
 * "pick one and close" surface with its own upload fold and confirm step,
 * a different concern from a full-page library - but the chip idiom (a
 * `Button` toggled by `aria-pressed`) is carried over on purpose, so the two
 * screens that narrow the same library read as the same gesture.
 */

export interface MediathequeToolbarProps {
  query: string;
  kind: AmbianceAssetKind | 'all';
  onQueryChange: (query: string) => void;
  onKindChange: (kind: AmbianceAssetKind | 'all') => void;
}

/**
 * The two natures' words come from `ASSET_KIND_LABEL` - the same catalogue
 * `mediatheque-card.tsx`'s badge already reads - rather than spelled a second
 * time here, so the toggle and the badge cannot drift apart on a rewording.
 */
const CHIPS: { value: AmbianceAssetKind | 'all'; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'image', label: ASSET_KIND_LABEL.image },
  { value: 'sound', label: ASSET_KIND_LABEL.sound },
];

export function MediathequeToolbar({
  query,
  kind,
  onQueryChange,
  onKindChange,
}: MediathequeToolbarProps) {
  const searchId = useId();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label htmlFor={searchId} className="sr-only">
        Rechercher un fichier
      </label>
      <input
        id={searchId}
        data-testid="mediatheque-recherche"
        value={query}
        onChange={(event) => onQueryChange(event.currentTarget.value)}
        placeholder="Rechercher un fichier…"
        className="h-10 w-64 rounded-md border-2 border-gray-300 px-3 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none"
      />
      <fieldset className="flex gap-1 border-0 p-0">
        <legend className="sr-only">Filtrer par nature</legend>
        {CHIPS.map((chip) => (
          <Button
            key={chip.value}
            type="button"
            size="sm"
            variant={kind === chip.value ? 'default' : 'outline'}
            aria-pressed={kind === chip.value}
            data-testid={`mediatheque-filtre-${chip.value}`}
            onClick={() => onKindChange(chip.value)}
          >
            {chip.label}
          </Button>
        ))}
      </fieldset>
    </div>
  );
}
